import { AudioTextStreamer } from "./audio-text-streamer";
import { LiveAudioStreamer } from "./live-audio-streamer";
import { AudioGraph } from "./audio-graph";
import { StreamQueue } from "./stream-queue";
import { TranscriptManager } from "./transcript-manager";
import { getCurrentSessionForMix } from "../utils/session-helpers";
import { MediaSessionManager } from "../utils/media-session";
import { isAndroid, isIOS } from "../utils/device-detection";
import type {
  EventMedia,
  Mix,
  Session,
  Stream,
  StreamData,
  TextFragment,
  PlayMixResult,
  StreamMetrics,
  TranscriptionEvent,
  StreamInterface,
} from "../types/model.types";
import { PlayerState, StreamFormat } from "../types/model.types";
import type { AudioData } from "../types/model.types";
import {
  StreamMetadataError,
  StreamSystemError,
  WebSocketError,
  createUnknownError,
  withErrorData,
  SDKError,
} from "../types/error.types";
import { isLiveFormat, isTextStreamFormat, isUdpStream } from "../utils/type-helpers";
import {
  areWiredHeadphonesRequired,
  areWiredOrBluetoothHeadphonesRequired,
} from "../utils/audio-port-utils";

export type PlayerOptions = {
  onStateChange: (state: PlayerState, error: SDKError | null) => void;
  onTranscriptionEvent?: (
    event: TranscriptionEvent,
    fullTranscript: TranscriptionEvent[] | null,
  ) => void;
  onStreamMetrics?: (metrics: StreamMetrics) => void;
  onFrequencyData?: (frequencyData: number[]) => void;
  runAudioDeviceMonitor?: (start: boolean) => void;
  runScreenCaptureMonitor?: (start: boolean) => void;
  bindNetworkInterface?: (networkInterface: StreamInterface | null) => void;
  isFullTranscriptEnabled?: boolean; // Enable full transcript tracking (default: true)
  isScreenCaptureAllowed?: boolean; // Enable screen capture (default: false)
  isFrequencyAnalysisEnabled?: boolean; // Enable frequency analysis (default: false)
  immediateTextDisplay?: boolean; // Skip text fragment queueing and display immediately (default: false)
};

/**
 * A Player class that handles audio streaming using the Web Audio API.
 * This class is responsible for managing the playback state, a audio stream, and user interaction
 * for audio playback.
 */
export class Player {
  public static _instance: Player | null = null;

  private _options: PlayerOptions;

  private _state: PlayerState = PlayerState.Stopped;
  private _stream: Stream | null = null;
  private _mix: Mix | null = null;
  private _session: Session | null = null;
  private _currentCaption: string | null = null;
  private _eventMedia: EventMedia | null = null;

  // Audio processing properties
  private _audioGraph: AudioGraph | null = null;
  private _streamer: AudioTextStreamer | LiveAudioStreamer | null = null;
  private _textStreamer: AudioTextStreamer | null = null;
  private _sessionEndTimer: any | null = null;
  private _streamWaitingForNetwork: Stream | null = null;
  private _isMuted: boolean = false;

  // Queue manager for handling stream data and text fragments
  private _streamQueue: StreamQueue;

  // Transcript manager for handling transcription events
  private _transcriptManager: TranscriptManager;

  // MediaSession manager for lock screen controls
  private _mediaSession: MediaSessionManager;

  private _isFullTranscriptEnabled: boolean;
  private _isScreenCaptureAllowed: boolean;
  private _isFrequencyAnalysisEnabled: boolean;

  /**
   * Creates a new Player instance.
   */
  constructor(options: PlayerOptions) {
    this._options = options;

    this._isFullTranscriptEnabled = options.isFullTranscriptEnabled !== false;
    this._isScreenCaptureAllowed = options.isScreenCaptureAllowed === true;
    this._isFrequencyAnalysisEnabled =
      options.isFrequencyAnalysisEnabled === true;

    // Initialize transcript manager
    this._transcriptManager = new TranscriptManager({
      onTranscriptionEvent: this._options.onTranscriptionEvent,
      enableFullTranscript: this._isFullTranscriptEnabled,
    });

    // Initialize queue manager
    this._streamQueue = new StreamQueue({
      immediateTextDisplay: options.immediateTextDisplay,
      callbacks: {
        onAudioDataProcess: (audioData: AudioData) => {
          if (this._stream) {
            this._audioGraph?.processAudioData(audioData, this._stream?.format);
          }
        },
        onAudioEventProcess: (event: string) => {
          this._transcriptManager.processAudioEvent(event);
        },
        onTextFragmentProcess: (textFragment: TextFragment) => {
          this._transcriptManager.processSpeechEvent(textFragment);
        },
        onStreamMetricsProcess: (metrics: StreamMetrics) => {
          this._options.onStreamMetrics?.(metrics);
        },
      },
    });

    // Initialize audio graph
    this._audioGraph = new AudioGraph({
      onPlaybackFinished: () => {
        this._streamQueue.onPlaybackFinished();
      },
      onFrequencyData: this._options.onFrequencyData,
      onError: (error) => {
        // Surface AudioGraph errors immediately
        this._failAndStop(error);
      },
    });

    // Initialize MediaSession manager
    this._mediaSession = new MediaSessionManager();
    this.setupMediaSession();

    // Set up visibility change listener
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && this._audioGraph) {
        this._audioGraph.setVolume(this._isMuted ? 0 : 1);
      }
    });
  }

  /**
   * Mutes or unmutes audio output for the player.
   * @param isMuted true to mute, false to unmute
   */
  public mute(isMuted: boolean): void {
    this._isMuted = isMuted;
    this._audioGraph?.setVolume(isMuted ? 0 : 1);
  }

  private _updatePlayerState(newState: PlayerState): void {
    if (this._state === newState) {
      return;
    }

    this._state = newState;

    // Update MediaSession playback state to match player state
    this._updateMediaSessionPlaybackState();

    switch (this._state) {
      case PlayerState.Loading:
        this._options.onStateChange(PlayerState.Loading, null);
        break;
      case PlayerState.Playing:
        this._options.onStateChange(PlayerState.Playing, null);
        break;
      case PlayerState.Stopped:
        this._options.onStateChange(PlayerState.Stopped, null);
        break;
      case PlayerState.Finished:
        this._options.onStateChange(PlayerState.Finished, null);
        break;
      // PlayerState.Failed is handled by _failAndStop and handleNativeError
    }
  }

  /**
   * Updates MediaSession playback state based on current player state
   */
  private _updateMediaSessionPlaybackState(): void {
    if (!MediaSessionManager.isSupported()) {
      return;
    }

    switch (this._state) {
      case PlayerState.Playing:
      case PlayerState.Loading:
        this._mediaSession.setPlaybackState("playing");
        // On Android, ensure audio element is playing when state is set to playing
        if (isAndroid()) {
          const audioElement = this._audioGraph?.getAudioElement();
          if (audioElement) {
            audioElement.play().catch((error) => {
              console.error("Failed to play audio element on Android:", error);
            });
          }
        }
        break;
      case PlayerState.Stopped:
        // On iOS, always set to "none" to clear lock screen controls entirely
        // (iOS lock screen controls won't work for resuming anyway due to platform limitations)
        // On Android, set to "paused" if we have a stream so user can resume from lock screen
        if (isIOS()) {
          this._mediaSession.setPlaybackState("none");
        } else if (this._stream && this._mix) {
          this._mediaSession.setPlaybackState("paused");
        } else {
          this._mediaSession.setPlaybackState("none");
        }
        break;
      case PlayerState.Finished:
        this._mediaSession.setPlaybackState("none");
        break;
      case PlayerState.Failed:
        this._mediaSession.setPlaybackState("paused");
        break;
    }
  }

  /**
   * Gets the current playback state of the player.
   */
  get state(): PlayerState {
    return this._state;
  }

  /**
   * Gets the current active stream. null if no stream is active.
   */
  get stream(): Stream | null {
    return this._stream;
  }

  /**
   * Gets the current active mix. null if no mix is active.
   */
  get mix(): Mix | null {
    return this._mix;
  }

  /**
   * Gets the current active session. null if no session is active.
   */
  get session(): Session | null {
    return this._session;
  }

  /**
   * Gets the current caption associated with the audio content, if any.
   */
  get currentCaption(): string | null {
    return this._currentCaption;
  }

  get canPlay(): boolean {
    return (
      this._state !== PlayerState.Playing && this._state !== PlayerState.Loading
    );
  }

  private setupMediaSession(): void {
    if (!MediaSessionManager.isSupported()) {
      return;
    }

    // Get audio element from audio graph for Android-specific handling
    const audioElement = this._audioGraph?.getAudioElement() ?? null;

    this._mediaSession.setHandlers(
      {
        onPlay: () => {
          if (this._audioGraph && this._stream) {
            this.playStream(this._stream);
          }
        },
        onPause: () => {
          this.stop();
        },
        onStop: () => {
          this.stop();
        },
      },
      {
        audioElement,
      },
    );
  }

  /**
   * Updates MediaSession metadata when mix/session/stream changes
   * Handlers are set once in setupMediaSession() and don't need to be updated
   */
  private updateMediaSessionMetadata(): void {
    if (!MediaSessionManager.isSupported()) {
      return;
    }

    const language = this._stream?.langCode;
    const metadata = MediaSessionManager.createMetadata(
      this._mix,
      this._session,
      this._stream,
      language,
    );
    this._mediaSession.setMetadata(metadata);
  }

  /**
   * Stops the current playback.
   * MediaSession handlers and metadata are kept intact so controls remain visible.
   * Playback state is updated via _updatePlayerState which will set it to "none" or "paused".
   */
  public stop(): void {
    if (this._state === PlayerState.Stopped) {
      return;
    }

    try {
      this._unmountStream();
      this._options.runAudioDeviceMonitor?.(false);
      this._options.runScreenCaptureMonitor?.(false);
    } catch (error) {
      console.error("Error stopping player", error);
    }

    if (
      this._state !== PlayerState.Failed &&
      this._state !== PlayerState.Finished &&
      this._state
    ) {
      this._updatePlayerState(PlayerState.Stopped);
    }
    // MediaSession playback state will be updated by _updatePlayerState
    // We don't clear MediaSession here to keep controls visible (matching reference implementation)
  }

  public selectStream(stream: Stream, session: Session, mix: Mix): void {
    this._stream = stream;
    this._session = session;
    this._mix = mix;
    this.updateMediaSessionMetadata();
  }

  private _unmountStream(): void {
    if (this._state === PlayerState.Stopped) {
      return;
    }

    if (this._sessionEndTimer) {
      clearTimeout(this._sessionEndTimer);
      this._sessionEndTimer = null;
    }

    if (this._streamer) {
      this._streamer.stop();
      this._streamer = null;
    }

    if (this._textStreamer) {
      this._textStreamer.stop();
      this._textStreamer = null;
    }

    if (this._audioGraph) {
      this._audioGraph.stop();
    }

    // Clear the queue manager
    this._streamQueue.clear();

    // Clear the transcript manager
    this._transcriptManager.clearTranscript();

    this._streamWaitingForNetwork = null;
  }

  /**
   * Sets up and starts streaming from the provided stream
   * @param stream The stream to play
   */
  public playStream(stream: Stream): void {
    this._updatePlayerState(PlayerState.Loading);

    // Start silence audio immediately while user gesture is still valid (for mobile lock screen controls)
    this._audioGraph?.startSilenceAudio();

    this._unmountStream();

    this._stream = stream;
    this.updateMediaSessionMetadata();

    if (this._session && this._mix?.sessionsEnabled && this._session.endTime) {
      const endTime = new Date(this._session.endTime).getTime();
      const now = Date.now();
      const delay = endTime - now;
      // Maximum safe value for setTimeout is 2^31 - 1 (2147483647 ms)
      const MAX_SETTIMEOUT_DELAY = 2147483647;

      if (delay > 0 && delay <= MAX_SETTIMEOUT_DELAY) {
        console.log("[Player] Session end timer:", {
          endTime: this._session.endTime,
          now: new Date().toISOString(),
          delay: delay,
        });
        this._sessionEndTimer = setTimeout(() => {
          this._unmountStream();
          this._updatePlayerState(PlayerState.Finished);
        }, delay);
      } else if (delay > MAX_SETTIMEOUT_DELAY) {
        console.warn(
          "[Player] Session end delay exceeds maximum setTimeout value, skipping timer:",
          {
            endTime: this._session.endTime,
            now: new Date().toISOString(),
            delay: delay,
            maxDelay: MAX_SETTIMEOUT_DELAY,
          },
        );
      }
    }

    if (stream.networkInterface && this._options.bindNetworkInterface) {
      this._streamWaitingForNetwork = stream;
      this._options.bindNetworkInterface(stream.networkInterface);
    } else {
      this._options.bindNetworkInterface?.(null);
      this._startStreamPlayback(stream);
    }
  }

  private _startStreamPlayback(stream: Stream): void {
    this._options.runAudioDeviceMonitor?.(true);
    this._options.runScreenCaptureMonitor?.(true);

    // Set the format for the stream queue to ensure proper processing
    this._streamQueue.setFormat(stream.format);

    // Respect mute state by adjusting audio output
    if (this._audioGraph) {
      this._audioGraph.setVolume(this._isMuted ? 0 : 1);
    }

    // Validate delay settings and set buffer processor latency
    if (stream.delay) {
      // Throw error if delay mode is dynamic (not yet supported)
      if (stream.delay.mode === "dynamic") {
        const dynamicDelayError = withErrorData(
          StreamMetadataError.StreamInvalid,
          {
            streamId: stream.id,
            reason: "Dynamic delay mode is not yet supported",
            delayMode: stream.delay.mode,
          },
        );
        this._failAndStop(dynamicDelayError);
        return;
      }

      // Set target latency if mode is static and target is provided
      if (
        stream.delay.mode === "static" &&
        stream.delay.desired !== undefined &&
        this._audioGraph
      ) {
        console.log(
          `Setting target latency for stream ${stream.id} to ${stream.delay.desired}ms`,
        );
        this._audioGraph.setTargetLatency(stream.delay.desired);
      }
    }

    if (!stream.streamUrl) {
      const streamUnavailableError = withErrorData(
        StreamMetadataError.StreamInvalid,
        { streamId: stream.id },
      );
      this._failAndStop(streamUnavailableError);
      return;
    }

    try {
      const wsUrl = stream.streamUrl;

      // Use LiveAudioStreamer for live formats, AudioTextStreamer for others
      if (isLiveFormat(stream.format)) {
        this._streamer = new LiveAudioStreamer({
          url: isUdpStream(stream) ? stream.textStreamUrl! : wsUrl,
          subscribeId: isUdpStream(stream) ? stream.subscriptionId + ":live" : stream.subscriptionId,
          streamId: stream.id,
          format: stream.format,
          onStreamData: (streamData) => {
            this._processStreamData(streamData, stream.format);
          },
          onError: (error) => {
            // Try to create a more specific error based on the error type
            let sdkError: SDKError;
            if (error instanceof Event && error.type === "error") {
              // WebSocket or network related error
              sdkError = withErrorData(WebSocketError.ConnectionFailed, {
                url: wsUrl,
                reason: "Stream connection failed",
              });
            } else if (
              error &&
              typeof error === "object" &&
              "message" in error
            ) {
              // Check if it's already an SDKError
              if ("group" in error && "code" in error) {
                sdkError = error as SDKError;
              } else {
                // Create a stream invalidated error with details
                sdkError = withErrorData(StreamMetadataError.StreamInvalid, {
                  streamId: stream.id,
                  originalError: error,
                });
              }
            } else {
              // Fallback to unknown error
              sdkError = createUnknownError(error, {
                context: "live_streamer_error",
                streamId: stream.id,
                streamUrl: wsUrl,
              });
            }
            this._failAndStop(sdkError);
          },
          onConnected: () => {
            this._updatePlayerState(PlayerState.Playing);
            // MediaSession playback state will be updated by _updatePlayerState
          },
          // Enable packet loss simulation for live streams
          packetLossSimulation: {
            enabled: false,
            lossRate: 0.0, // 5% packet loss
            burstLength: 0,
          },
        });
      } else {
        this._streamer = new AudioTextStreamer({
          url: wsUrl,
          subscribeId: stream.subscriptionId,
          streamId: stream.id,
          format: stream.format,
          onStreamData: (streamData) => {
            this._processStreamData(streamData, stream.format);
          },
          onError: (error) => {
            // Try to create a more specific error based on the error type
            let sdkError: SDKError;
            if (error instanceof Event && error.type === "error") {
              // WebSocket or network related error
              sdkError = withErrorData(WebSocketError.ConnectionFailed, {
                url: wsUrl,
                reason: "Stream connection failed",
              });
            } else if (
              error &&
              typeof error === "object" &&
              "message" in error
            ) {
              // Check if it's already an SDKError
              if ("group" in error && "code" in error) {
                sdkError = error as SDKError;
              } else {
                // Create a stream invalidated error with details
                sdkError = withErrorData(StreamMetadataError.StreamInvalid, {
                  streamId: stream.id,
                  originalError: error,
                });
              }
            } else {
              // Fallback to unknown error
              sdkError = createUnknownError(error, {
                context: "streamer_error",
                streamId: stream.id,
                streamUrl: wsUrl,
              });
            }
            this._failAndStop(sdkError);
          },
          onConnected: () => {
            this._updatePlayerState(PlayerState.Playing);
            // MediaSession playback state will be updated by _updatePlayerState
          },
        });
      }

      this._streamer.start();

      if (
        isTextStreamFormat(stream.format) &&
        stream.textStreamUrl &&
        stream.textSubscriptionId
      ) {
        this._textStreamer = new AudioTextStreamer({
          url: stream.textStreamUrl,
          subscribeId: stream.textSubscriptionId,
          streamId: stream.id,
          format: StreamFormat.LIVE_TEXT,
          onStreamData: (streamData) => {
            this._processStreamData(streamData, stream.format);
          },
          onError: (error) => {
            console.error("Text streamer error:", error);
            // For text-only errors, we don't fail the entire player
            // Just log the error for debugging
          },
        });
        this._textStreamer.start();
      }
    } catch (error) {
      const unknownError = createUnknownError(error, {
        context: "_startStreamPlayback",
        streamId: stream.id,
        streamUrl: stream.streamUrl,
      });
      this._failAndStop(unknownError);
    }
  }

  private _processStreamData(
    streamData: StreamData,
    format: StreamFormat,
  ): void {
    // Set the format for the queue manager if not already set
    this._streamQueue.setFormat(format);

    // Let the queue manager handle all processing
    this._streamQueue.enqueueStreamData(streamData);
  }

  public play(mix: Mix): PlayMixResult | null {
    if (!mix.active) {
      this._updatePlayerState(PlayerState.Loading);
      const error = withErrorData(StreamMetadataError.MixIsInactive, {
        mixId: mix.id,
      });
      this._failAndStop(error);
      return null;
    }

    const currentSession = getCurrentSessionForMix(mix);

    if (!currentSession || !currentSession.streams) {
      this._updatePlayerState(PlayerState.Loading);
      const error = withErrorData(StreamMetadataError.StreamInvalid, {
        mixId: this._mix!.id,
      });
      this._failAndStop(error);
      return null;
    }

    const result: PlayMixResult = {
      mix,
      session: currentSession,
      stream: null,
      selectStream: null,
    };

    if (currentSession.streams.length == 1) {
      result.stream = currentSession.streams[0];
      this._mix = mix;
      this._session = currentSession;
      this.updateMediaSessionMetadata();
      this.playStream(result.stream);
    } else {
      result.selectStream = (stream: Stream) => {
        this._mix = mix;
        this._session = currentSession;
        this.updateMediaSessionMetadata();
        this.playStream(stream);
      };
    }

    return result;
  }

  /**
   * Completely stops the player and releases resources.
   */
  public destroy(): void {
    this.stop();
    this._mediaSession.clear();
    if (this._audioGraph) {
      this._audioGraph.destroy();
      this._audioGraph = null;
    }
    this._options.bindNetworkInterface?.(null);
    this._options.runAudioDeviceMonitor?.(false);
    this._options.runScreenCaptureMonitor?.(false);
  }

  /**
   * Checks if the player is currently playing.
   */
  public isPlaying(): boolean {
    return this._state === PlayerState.Playing;
  }

  onAudioDeviceChanged(deviceType: string): void {
    if (!this._eventMedia) {
      return;
    }

    if (!this._eventMedia.allowedOutputTypes.includes(deviceType)) {
      const audioDeviceError = withErrorData(
        StreamSystemError.AudioOutputPortForbidden,
        {
          portType: deviceType,
          allowedOutputTypes: this._eventMedia.allowedOutputTypes,
          areWiredHeadphonesRequired: areWiredHeadphonesRequired(
            this._eventMedia.allowedOutputTypes,
          ),
          areWiredOrBluetoothHeadphonesRequired:
            areWiredOrBluetoothHeadphonesRequired(
              this._eventMedia.allowedOutputTypes,
            ),
        },
      );
      this._failAndStop(audioDeviceError);
    }
  }

  onScreenCaptureStatusChanged(isScreenCaptureActive: boolean): void {
    if (isScreenCaptureActive && !this._isScreenCaptureAllowed) {
      const screenCaptureError = withErrorData(
        StreamSystemError.ScreenCaptureActive,
        { isScreenCaptureActive: isScreenCaptureActive },
      );
      this._failAndStop(screenCaptureError);
    }
  }

  /**
   * Called by the native layer (Android/iOS) to report an error that occurred
   * outside the normal JS flow (e.g.
   *   - failure to bind a network interface
   *   - audio-device monitoring problems, etc.)
   * This transitions the player to Failed state and emits the usual events.
   * @param error A human-readable error message.
   */
  public handleNativeError(error: string): void {
    if (this._state !== PlayerState.Failed) {
      let errorToReport: SDKError;

      try {
        const errorObj = JSON.parse(error);
        // Check if it's a valid SDKError structure
        if (errorObj.group && errorObj.code && errorObj.message) {
          console.error(
            `Native Error: ${errorObj.group}.${errorObj.code} - ${errorObj.message}`,
          );
          errorToReport = errorObj; // Use the parsed error as-is
        } else {
          throw new Error("Invalid SDKError structure");
        }
      } catch (e) {
        console.error(
          "Failed to parse native error object, creating unknown error",
          e,
        );
        // Create an unknown error with the original error data
        const unknownError = createUnknownError(null, {
          originalErrorString: error,
          parseError: e instanceof Error ? e.message : "Unknown parse error",
          context: "handleNativeError",
        });
        errorToReport = unknownError;
      }

      this._failAndStop(errorToReport);
    }
  }

  private _failAndStop(error: SDKError): void {
    this._unmountStream();
    if (this._state !== PlayerState.Failed) {
      this._state = PlayerState.Failed;
      this._options.onStateChange(PlayerState.Failed, error);
    }
  }

  /**
   * Called by the native layer when a network binding has successfully completed.
   */
  public networkBindingSucceeded() {
    if (this._streamWaitingForNetwork) {
      const stream = this._streamWaitingForNetwork;
      this._streamWaitingForNetwork = null;
      this._startStreamPlayback(stream);
    }
  }

  /**
   * Gets the current full transcript
   */
  public getFullTranscript(): TranscriptionEvent[] {
    return this._transcriptManager.getFullTranscript();
  }

  /**
   * Clears the current transcript
   */
  public clearTranscript(): void {
    this._transcriptManager.clearTranscript();
  }

  /**
   * Enables or disables full transcript tracking
   */
  public setEnableFullTranscript(enabled: boolean): void {
    this._isFullTranscriptEnabled = enabled;
    this._transcriptManager.options.enableFullTranscript = enabled;
  }

  /**
   * Gets whether full transcript tracking is enabled
   */
  public get isFullTranscriptEnabled(): boolean {
    return this._isFullTranscriptEnabled;
  }

  /**
   * Sets whether full transcript tracking is enabled
   */
  public setIsFullTranscriptEnabled(enabled: boolean): void {
    this.setEnableFullTranscript(enabled);
  }

  /**
   * Sets whether screen capture is allowed
   */
  public setIsScreenCaptureAllowed(allowed: boolean): void {
    this._isScreenCaptureAllowed = allowed;
  }

  /**
   * Gets whether screen capture is allowed
   */
  public get isScreenCaptureAllowed(): boolean {
    return this._isScreenCaptureAllowed;
  }

  /**
   * Sets whether frequency analysis is enabled
   * Note: This only updates the property. To fully enable/disable frequency analysis,
   * the AudioGraph would need to be recreated with the appropriate callback.
   */
  public setIsFrequencyAnalysisEnabled(enabled: boolean): void {
    this._isFrequencyAnalysisEnabled = enabled;
  }

  /**
   * Gets whether frequency analysis is enabled
   */
  public get isFrequencyAnalysisEnabled(): boolean {
    return this._isFrequencyAnalysisEnabled;
  }
}
